type VersionedData<T> = {
  version: number;
  data: T;
};

type useLocalStorageProps<T> = {
  key: string;
  version: number;
  migrator?: (oldData: any, oldVersion: number) => T;
};

export const useLocalStorage = <T>({ key, version, migrator }: useLocalStorageProps<T>) => {
  const getData = (): T | null => {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    try {
      const parsed: VersionedData<T> = JSON.parse(raw);
      if (parsed.version !== version) {
        console.warn(`Version mismatch for key "${key}". Expected version ${version}, got ${parsed.version}.`);
        return migrator ? migrator(parsed.data, parsed.version) : parsed.data;
      }
      return parsed.data;
    } catch (error) {
      console.error(`Failed to parse LocalStorage data for key "${key}":`, error);
      return null;
    }
  };

  const setData = (data: T) => {
    const versionedData: VersionedData<T> = {
      version: version,
      data,
    };

    localStorage.setItem(key, JSON.stringify(versionedData));
  };

  const clearData = () => {
    localStorage.removeItem(key);
  };

  return { getData, setData, clearData };
};
